import { memo } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import type { TableOptions } from '@tanstack/react-table';

import { IconWithDropdown } from 'components/common/Inputs';
import { useTranslation } from 'react-i18next';
import type { DataSchema, HeliosRowControls, Schema } from 'types';
import type { SxProps, Theme } from '@mui/material';
import { IconButton, Tooltip } from '@mui/material';
import type { RowControls } from 'types/table';
import { StyledTableCell } from './TableRowHeliosFunctionsControls';

const getDirectIconButton = <T,>(
  row: T,
  control: {
    icon: SvgIconComponent;
    onClick: (row: T) => void;
    label: string;
    disabled: boolean | undefined;
    id: string;
    showIconDirectly: boolean | undefined;
  }
) => {
  const Icon = control.icon;
  return (
    <Tooltip key={control.id} title={control.label}>
      <IconButton onClick={() => control.onClick(row)} disabled={control.disabled} data-testid={control.id}>
        <Icon />
      </IconButton>
    </Tooltip>
  );
};

interface TableRowControlsProps<T extends keyof P, P extends DataSchema> {
  rowControls?: RowControls<TableOptions<Schema<T, P>>['data'][number]>;

  tableControls?: {
    [key: string]: {
      visible: boolean;
      disabled?: boolean;
      onClick: () => void;
      icon: SvgIconComponent;
      label: string;
      id: string;
    };
  };

  heliosRowControls?: HeliosRowControls<TableOptions<Schema<T, P>>['data'][number]>;

  row: TableOptions<Schema<T, P>>['data'][number];
  noTableCell?: boolean;
  sx?: SxProps<Theme>;
  testIdPrefix?: string;
}

const TableRowControlsBase = <T extends keyof P, P extends DataSchema>(props: TableRowControlsProps<T, P>) => {
  const { t } = useTranslation();
  const { rowControls, heliosRowControls, row, noTableCell, sx, testIdPrefix } = props;

  const rowControlsValues = Object.entries(rowControls ?? {})

    .filter(([_, value]) => value.visible)
    .map(([key, value]) => ({
      icon: value.icon,
      onClick: value.onClick,
      label: value.label,
      disabled: typeof value.disabled === 'function' ? value.disabled(row) : value.disabled,
      id: key,
      showIconDirectly: value.showIconDirectly,
      dataTestId: value.dataTestId,
      iconDataTestId: value.iconDataTestId,
      textDataTestId: value.textDataTestId,
      menuDataTestId: value.menuDataTestId,
      menuTriggerDataTestId: typeof value.menuTriggerDataTestId === 'function' ? value.menuTriggerDataTestId(row) : value.menuTriggerDataTestId,
    }));

  const heliosRowControlsValues = Object.entries(heliosRowControls ?? {})

    .filter(([_, value]) => {
      const visible = typeof value.visible === 'function' ? value.visible(row) : value.visible;
      return visible;
    })
    .map(([key, value]) => ({
      icon: value.icon,
      onClick: value.onClick,
      label: value.label,
      disabled: typeof value.disabled === 'function' ? value.disabled(row) : value.disabled,
      id: key,
      showIconDirectly: value.showIconDirectly,
      dataTestId: value.dataTestId,
      iconDataTestId: value.iconDataTestId,
      textDataTestId: value.textDataTestId,
      menuDataTestId: value.menuDataTestId,
      menuTriggerDataTestId: typeof value.menuTriggerDataTestId === 'function' ? value.menuTriggerDataTestId(row) : value.menuTriggerDataTestId,
    }));

  const allControls = [...rowControlsValues, ...heliosRowControlsValues];

  const directControls = allControls.filter((c) => c.showIconDirectly);
  const dropdownControls = allControls.filter((c) => !c.showIconDirectly);

  const content = (
    <>
      {directControls.map((control) =>
        getDirectIconButton(row, {
          ...control,
          id: testIdPrefix ? `${testIdPrefix}-row-control-${control.id}` : control.id,
        })
      )}
      {dropdownControls.length > 0 && (
        <IconWithDropdown
          returnValue={row}
          values={dropdownControls}
          helperText={t('common.rowControls')}
          sx={sx}
          dataTestId={testIdPrefix ? `${testIdPrefix}-row-controls-menu-button` : 'table-row-controls-menu-button'}
        />
      )}
    </>
  );

  if (noTableCell) return content;

  return (
    <StyledTableCell align="right" sx={{ whiteSpace: 'nowrap', ...sx }}>
      {content}
    </StyledTableCell>
  );
};

export const TableRowControls = memo(TableRowControlsBase) as typeof TableRowControlsBase;
